import type { ReactElement } from 'react';
import { useState } from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';

import { BootSequence, shouldPlayBoot } from '@/components/BootSequence';
import { EasterEggs } from '@/components/EasterEggs';
import { Layout } from '@/components/Layout';
import { RequireAuth } from '@/components/RequireAuth';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { CallbackPage } from '@/pages/auth/CallbackPage';
import { LoginPage } from '@/pages/auth/LoginPage';
import { DocsLanding } from '@/pages/docs/DocsLanding';
import { DocsLayout } from '@/pages/docs/DocsLayout';
import { DocsPage } from '@/pages/docs/DocsPage';
import { DashboardPage } from '@/pages/insights/DashboardPage';
import { InvestigateLivePage } from '@/pages/investigate/InvestigateLivePage';
import { InvestigationDetailPage } from '@/pages/investigate/InvestigationDetailPage';
import { InvestigationsListPage } from '@/pages/investigate/InvestigationsListPage';
import { IssueDetailPage } from '@/pages/issues/IssueDetailPage';
import { IssueListPage } from '@/pages/issues/IssueListPage';
import { MemoryDashboard } from '@/pages/Memory/MemoryDashboard';
import { IntakeWizardPage } from '@/pages/intake/IntakeWizardPage';
import { RequirementDetailPage } from '@/pages/requirements/RequirementDetailPage';
import { RequirementsPage } from '@/pages/requirements/RequirementsPage';
import { NotFoundPage } from '@/pages/NotFound';
import { TeamsPage } from '@/pages/teams/TeamsPage';
import { PatsPage } from '@/pages/settings/PatsPage';
import { SharePage } from '@/pages/share/SharePage';
import { NotificationsPage } from '@/pages/notifications/NotificationsPage';
import { ScorecardPage } from '@/pages/scorecard/ScorecardPage';
import { ReviewsListPage } from '@/pages/reviews/ReviewsListPage';
import { ReviewDetailPage } from '@/pages/reviews/ReviewDetailPage';
import { ReviewReposPage } from '@/pages/reviews/ReviewReposPage';
import { IntegrationsPage } from '@/pages/integrations/IntegrationsPage';

function TitleSync(): null {
  useDocumentTitle();
  return null;
}

export function App(): ReactElement {
  const [bootDone, setBootDone] = useState<boolean>(() => !shouldPlayBoot());

  return (
    <>
      {!bootDone && <BootSequence onDone={() => setBootDone(true)} />}
      <EasterEggs />
      <TitleSync />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/auth/callback" element={<CallbackPage />} />
        {/* Public share view — no auth guard; token in path is the credential */}
        <Route path="/share/:token" element={<SharePage />} />

        <Route
          element={
            <RequireAuth>
              <Layout />
            </RequireAuth>
          }
        >
          <Route path="/" element={<Navigate to="/insights" replace />} />
          <Route path="/insights" element={<DashboardPage />} />
          <Route path="/issues" element={<IssueListPage />} />
          <Route path="/issues/:id" element={<IssueDetailPage />} />
          <Route path="/investigate" element={<InvestigateLivePage />} />
          <Route path="/investigations" element={<InvestigationsListPage />} />
          <Route path="/investigations/:id" element={<InvestigationDetailPage />} />
          {/* /memory is open to all authenticated users (read-only browse);
              individual write actions inside the page are gated to the
              admin on the backend AND visually disabled in the FE. */}
          <Route path="/memory" element={<MemoryDashboard />} />
          {/* Sherlock Reviewer — /reviews is the list, /reviews/:id the
              detail (SSE tail while running), /reviews/repos the registry
              admin sub-route (reads open to all; writes admin-only inside
              the page, same pattern as /teams). Static "repos" outranks
              the dynamic :id segment in React Router v6. */}
          <Route path="/reviews" element={<ReviewsListPage />} />
          <Route path="/reviews/repos" element={<ReviewReposPage />} />
          <Route path="/reviews/:id" element={<ReviewDetailPage />} />
          {/* Deliver — /scorecard is the KPI scorecard (all authenticated users).
              /kpis redirects to /scorecard for any bookmarks. */}
          <Route path="/scorecard" element={<ScorecardPage />} />
          <Route path="/kpis" element={<Navigate to="/scorecard" replace />} />
          {/* Deliver — /requests is the list + analytics; /requests/new is the
              capture wizard; /requests/:id is a dedicated read-only detail view.
              Reads open to all; archive is admin-only inside the list page.
              Static segments (new) outrank the dynamic :id segment in React
              Router v6, so /requests/new resolves to the wizard, not the detail
              route. The old /requirements + /intake paths redirect so existing
              links and bookmarks keep working. */}
          <Route path="/requests" element={<RequirementsPage />} />
          <Route path="/requests/new" element={<IntakeWizardPage />} />
          <Route path="/requests/new/:sessionId" element={<IntakeWizardPage />} />
          <Route path="/requests/:id" element={<RequirementDetailPage />} />
          <Route path="/requirements" element={<Navigate to="/requests" replace />} />
          <Route path="/intake" element={<Navigate to="/requests/new" replace />} />
          {/* /teams is admin-only in the nav but the route itself is
              accessible to all authenticated users — the page handles
              non-admin gating with a read-only banner and hidden write buttons. */}
          <Route path="/teams" element={<TeamsPage />} />
          {/* /notifications is admin-only in the nav. The log tab returns 403
              for non-admins (backend enforces); the ruleset tab is read-only
              for non-admins. Non-admin gating is handled inside the page. */}
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/settings/pats" element={<PatsPage />} />
          {/* /integrations is admin-only in the nav but the route itself is
              accessible to all authenticated users — the page handles
              non-admin gating with a read-only banner and hidden controls. */}
          <Route path="/integrations" element={<IntegrationsPage />} />
          <Route path="/docs" element={<DocsLayout />}>
            <Route index element={<DocsLanding />} />
            <Route path=":slug" element={<DocsPage />} />
          </Route>
          <Route path="*" element={<NotFoundPage />} />
        </Route>
      </Routes>
    </>
  );
}
